import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Sun, Info, MapPin, Zap, AlertTriangle, Loader2, Gauge, ThermometerSnowflake, Radiation, Mountain, Activity, MoreVertical, RefreshCcw, Clock } from 'lucide-react';
import { fetchPVGISEstimate, fetchActualProductionComparison } from '@/api/pvgis';
import { useI18n } from '@/i18n';
import toast from 'react-hot-toast';
import {
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart
} from 'recharts';
import { useErrorToast, LoadRetry } from '@/lib/notify';

export default function PVGISPage() {
  const { t } = useI18n();
  const { uuid_id } = useParams<{ uuid_id: string }>();

  const [data, setData] = useState<any>(null);
  const [actualData, setActualData] = useState<any>(null);
  const [currentYearData, setCurrentYearData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [refreshing, setRefreshing] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const loadData = async (shouldRefresh = false) => {
    if (!uuid_id) return;
    if (shouldRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const currentYear = new Date().getFullYear();
      const [result, lastYearResult, currentYearResult] = await Promise.all([
        fetchPVGISEstimate(uuid_id, shouldRefresh),
        fetchActualProductionComparison(uuid_id, currentYear - 1),
        fetchActualProductionComparison(uuid_id, currentYear)
      ]);

      setData(result);
      setActualData(lastYearResult);
      setCurrentYearData(currentYearResult);
      if (shouldRefresh) toast.success(t('pvgis.refreshSuccess') || 'Veriler güncellendi');
    } catch (err) {
      console.error(err);
      setError(t('pvgis.loadError'));
    } finally {
      setLoading(false);
      setRefreshing(false);
      setShowActions(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [uuid_id, t]);

  const handleRefresh = () => {
    loadData(true);
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-amber-500" />
          <p className="text-gray-500 font-medium">{t('pvgis.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) return <LoadRetry className="h-full" />;

  if (!data || !data?.plant) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-center max-w-md">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-lg font-semibold text-red-800">{t('common.notFound')}</h2>

        </div>
      </div>
    );
  }

  // --- Data Normalization ---
  const monthlyData = (data.estimate?.outputs?.monthly as any)?.fixed || data.estimate?.outputs?.monthly || [];
  const monthlyProduction = Array.isArray(monthlyData) ? monthlyData.map((m: any, index: number) => {
    const monthIndex = m.month || (index + 1);
    const lastYearEntry = actualData?.data?.find((a: any) => a.month === monthIndex);
    const currentYearEntry = currentYearData?.data?.find((a: any) => a.month === monthIndex);

    return {
      name: t(`common.months.${monthIndex}`),
      production: Number(Number(m.E_m || 0).toFixed(1)),
      actualProduction: lastYearEntry ? Number(lastYearEntry.production.toFixed(1)) : null,
      currentYearProduction: currentYearEntry && currentYearEntry.production > 0 ? Number(currentYearEntry.production.toFixed(1)) : null,
      irradiation: Number(Number(m["H(i)_m"] || 0).toFixed(1))
    };
  }) : [];

  const totalActual = actualData?.data?.reduce((acc: number, curr: any) => acc + curr.production, 0) || 0;
  const totalCurrent = currentYearData?.data?.reduce((acc: number, curr: any) => acc + curr.production, 0) || 0;
  const totals = data.estimate?.outputs?.totals?.fixed || {};

  return (
    <div className="flex flex-col gap-6 overflow-y-auto flex-1 min-h-0 pb-6 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">

          <div className="flex flex-col">
            <h1 className="text-xl font-semibold text-slate-900">
              {data.plant.name} - {t('pvgis.title')}
            </h1>
            <div className="flex items-center gap-2">
               <p className="text-sm text-slate-500">{t('pvgis.subtitle')}</p>
               {data.plant.updated_at && (
                 <div className="flex items-center gap-1.5 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-600">
                   <Clock className="h-3 w-3" />
                   {new Date(data.plant.updated_at).toLocaleString('tr-TR')}
                 </div>
               )}
            </div>
          </div>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-500 shadow-sm transition hover:bg-slate-50 active:scale-95"
          >
            <MoreVertical size={18} />
          </button>

          {showActions && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)} />
              <div className="absolute right-0 top-full z-20 mt-2 w-48 origin-top-right rounded-2xl border border-slate-100 bg-white p-1.5 shadow-xl shadow-slate-200/50 animate-in fade-in zoom-in-95 duration-200">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-amber-50 hover:text-amber-700 disabled:opacity-50"
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCcw className="h-4 w-4" />
                  )}
                  Verileri Yenile
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-lg bg-blue-50 p-2 text-blue-600">
              <Zap className="h-5 w-5" />
            </div>
            <span className="text-sm font-medium text-slate-500">{t('pvgis.yearlyProduction')}</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900">{Number(totals.E_y || 0).toLocaleString()}</span>
            <span className="text-sm font-medium text-slate-400">{t('pvgis.kwhPerYear')}</span>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-lg bg-orange-50 p-2 text-orange-600">
              <Sun className="h-5 w-5" />
            </div>
            <span className="text-sm font-medium text-slate-500">{t('pvgis.averageIrradiation')}</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900">{Number(totals["H(i)_y"] || 0).toLocaleString()}</span>
            <span className="text-sm font-medium text-slate-400">{t('pvgis.kwhPerM2')}</span>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-lg bg-red-50 p-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <span className="text-sm font-medium text-slate-500">{t('pvgis.systemLoss')}</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900">{Math.abs(Number(totals.l_total || 0)).toFixed(1)}</span>
            <span className="text-sm font-medium text-slate-400">%</span>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-lg bg-slate-50 p-2 text-slate-600">
              <MapPin className="h-5 w-5" />
            </div>
            <span className="text-sm font-medium text-slate-500">{t('pvgis.locationAndPower')}</span>
          </div>
          <div className="text-sm text-slate-600 font-medium">
            {Number(data.plant.lat || 0).toFixed(4)}, {Number(data.plant.lon || 0).toFixed(4)}
            <br />
            {Number(data.plant.peakpower || 0).toLocaleString()} {t('pvgis.installedPower')}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm flex flex-col min-h-[450px]">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-2">
            <h3 className="text-lg font-semibold text-slate-900">{t('pvgis.monthlyProductionChart')}</h3>
            <div className="flex items-center gap-2 text-xs text-slate-400 italic bg-slate-50 px-3 py-1 rounded-full">
              <Info className="h-3 w-3 shrink-0" />
              {t('pvgis.infoNote')}
            </div>
          </div>
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthlyProduction} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis 
                   dataKey="name" 
                   axisLine={false} 
                   tickLine={false} 
                   tick={{ fontSize: 11, fill: '#64748b', fontWeight: 500 }} 
                   dy={10}
                />
                <YAxis 
                   yAxisId="left"
                   orientation="left"
                   axisLine={false} 
                   tickLine={false} 
                   tick={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }} 
                />
                <YAxis 
                   yAxisId="right"
                   orientation="right"
                   axisLine={false} 
                   tickLine={false} 
                   tick={{ fontSize: 11, fill: '#3b82f6', fontWeight: 600 }} 
                />
                <Tooltip
                  contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', padding: '12px' }}
                  cursor={{ fill: 'rgba(245, 158, 11, 0.05)' }}
                />
                <Legend verticalAlign="top" height={36}/>
                <Bar 
                   yAxisId="left"
                   dataKey="production" 
                   fill="#f59e0b" 
                   radius={[6, 6, 0, 0]} 
                   name={t('pvgis.productionLabel') + " (Tahmin)"} 
                   barSize={40}
                />
                <Line 
                   yAxisId="left"
                   type="monotone" 
                   dataKey="actualProduction" 
                   stroke="#10b981" 
                   strokeWidth={3}
                   dot={{ r: 4, strokeWidth: 2, fill: 'white' }}
                   name={`Gerçek (${new Date().getFullYear() - 1})`} 
                />
                <Line 
                   yAxisId="left"
                   type="monotone" 
                   dataKey="currentYearProduction" 
                   stroke="#6366f1" 
                   strokeWidth={3}
                   dot={{ r: 4, strokeWidth: 2, fill: 'white' }}
                   name={`Gerçek (${new Date().getFullYear()})`} 
                />
                <Line 
                   yAxisId="right"
                   type="monotone" 
                   dataKey="irradiation" 
                   stroke="#3b82f6" 
                   strokeWidth={3}
                   dot={{ r: 4, strokeWidth: 2, fill: 'white' }}
                   name={t('pvgis.averageIrradiation')} 
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm flex flex-col gap-6">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-indigo-500" />
            <h3 className="text-lg font-semibold text-slate-900">{t('pvgis.lossAnalysis')}</h3>
          </div>

          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between p-3 rounded-xl bg-amber-50">
               <div className="flex items-center gap-3">
                  <Activity size={18} className="text-amber-600" />
                  <span className="text-sm font-medium text-slate-700">{t('pvgis.miscLoss')}</span>
               </div>
               <span className="text-lg font-bold text-amber-700">-%{data.plant.system_loss || 14}</span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-sky-50">
               <div className="flex items-center gap-3">
                  <ThermometerSnowflake size={18} className="text-sky-600" />
                  <span className="text-sm font-medium text-slate-700">{t('pvgis.tempLoss')}</span>
               </div>
               <span className="text-lg font-bold text-sky-700">-%{Math.abs(Number(totals.l_tg || 0)).toFixed(1)}</span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-orange-50">
               <div className="flex items-center gap-3">
                  <Radiation size={18} className="text-orange-600" />
                  <span className="text-sm font-medium text-slate-700">{t('pvgis.reflectionLoss')}</span>
               </div>
               <span className="text-lg font-bold text-orange-700">-%{Math.abs(Number(totals.l_re || 0)).toFixed(1)}</span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-stone-50">
               <div className="flex items-center gap-3">
                  <Mountain size={18} className="text-stone-600" />
                  <span className="text-sm font-medium text-slate-700">{t('pvgis.shadingLoss')}</span>
               </div>
               <span className="text-lg font-bold text-stone-700">-%{Math.abs(Number(totals.l_sh || 0)).toFixed(1)}</span>
            </div>

            <div className="mt-4 pt-6 border-t border-slate-100">
               <div className="flex items-center justify-between px-2">
                  <span className="text-sm font-bold text-slate-900">{t('pvgis.totalLoss')}</span>
                  <span className="text-xl font-bold text-red-600">-%{Math.abs(Number(totals.l_total || 0)).toFixed(1)}</span>
               </div>
            </div>
          </div>

          <div className="mt-auto p-4 rounded-xl bg-indigo-50/50 border border-indigo-100/50">
             <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">{t('pvgis.dailyProduction')} (PVGIS)</span>
                <span className="text-lg font-bold text-indigo-900">{Number(totals.E_d || 0).toFixed(1)} kWh</span>
             </div>
          </div>
        </div>
      </div>

      {/* Production Comparison Table */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
             <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
                <Activity size={20} />
             </div>
             <h3 className="text-lg font-semibold text-slate-900">Aylık Üretim Kıyaslaması (kWh)</h3>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-end">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Yıllık Tahmin (PVGIS)</span>
               <span className="text-sm font-black text-amber-600">{Number(totals.E_y || 0).toLocaleString()}</span>
            </div>
            <div className="h-8 w-px bg-slate-100" />
            <div className="flex flex-col items-end">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Gerçek ({new Date().getFullYear() - 1})</span>
               <span className="text-sm font-black text-emerald-600">{totalActual.toLocaleString()}</span>
            </div>
            <div className="h-8 w-px bg-slate-100" />
            <div className="flex flex-col items-end">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Gerçek ({new Date().getFullYear()})</span>
               <span className="text-sm font-black text-indigo-600">{totalCurrent.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {monthlyProduction.map((m: any, index: number) => (
            <div key={index} className="flex flex-col gap-3 p-4 rounded-2xl border border-slate-50 bg-slate-50/30 hover:bg-white hover:border-slate-200 hover:shadow-md transition-all group">
               <span className="text-xs font-black text-slate-900 border-b border-slate-100 pb-2">{m.name}</span>

               <div className="flex flex-col gap-2">
                 <div className="flex flex-col">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Tahmin</span>
                    <span className="text-sm font-bold text-amber-600 font-mono">{m.production.toLocaleString()}</span>
                 </div>
                 <div className="flex flex-col">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Gerçek ({new Date().getFullYear() - 1})</span>
                    <span className="text-sm font-bold text-emerald-600 font-mono">
                      {m.actualProduction !== null ? m.actualProduction.toLocaleString() : '-'}
                    </span>
                 </div>
                 <div className="flex flex-col border-t border-slate-100 pt-1 mt-1">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Gerçek ({new Date().getFullYear()})</span>
                    <span className="text-sm font-bold text-indigo-600 font-mono">
                      {m.currentYearProduction !== null ? m.currentYearProduction.toLocaleString() : '-'}
                    </span>
                 </div>
               </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
